import { useActiveProjectId } from '@lifeomic/phc-web-toolkit/src/hooks';
import React, {
  Dispatch,
  FC,
  SetStateAction,
  createContext,
  useEffect,
  useState,
} from 'react';
import {
  VariantSelection,
  useSelectedVariants,
} from './FilterResultsManager/useSelectedVariants';
import { useParams } from 'react-router-dom';
import { SelectedVariant } from './FilterResultsManager/types';

type SelectedVariantsContextValues = {
  createTargetedVariant: (selectedVariant: SelectedVariant) => Promise<string>;
  variantSelection: VariantSelection;
  selectedVariants: Partial<SelectedVariant>[];
  updateVariantSelection: Dispatch<SetStateAction<VariantSelection>>;
  refreshTargetedVariants: (
    diagnosticReport: fhir.DiagnosticReport,
  ) => Promise<string[]>;
  error: boolean;
  showSelectedVariants: boolean;
  setShowSelectedVariants: Dispatch<SetStateAction<boolean>>;
};

const defaultContextValues: SelectedVariantsContextValues = {
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  createTargetedVariant: () => undefined,
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  variantSelection: undefined,
  selectedVariants: [],
  updateVariantSelection: () => undefined,
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  refreshTargetedVariants: () => undefined,
  error: false,
  showSelectedVariants: false,
  setShowSelectedVariants: () => undefined,
};

export const SelectedVariantsContext =
  createContext<SelectedVariantsContextValues>(defaultContextValues);

export const SelectedVariantsProvider: FC<{ subjectId?: string }> = ({
  subjectId,
  children,
}) => {
  const projectId = useActiveProjectId();
  const { subjectId: subjectIdFromSlug } = useParams<{
    subjectId: string;
  }>();
  const resolvedSubjectId = subjectId || subjectIdFromSlug;
  const [showSelectedVariants, setShowSelectedVariants] =
    useState<boolean>(false);
  const [selectedVariants, setSelectedVariants] = useState<
    Partial<SelectedVariant>[]
  >([]);

  const {
    createTargetedVariant,
    variantSelection,
    updateVariantSelection,
    refreshTargetedVariants,
    error,
  } = useSelectedVariants({
    datasetId: projectId,
    subjectId: resolvedSubjectId,
  });

  useEffect(() => {
    if (!showSelectedVariants) {
      setSelectedVariants([]);
      return;
    }

    const variants = Object.values(variantSelection.variants);

    setSelectedVariants(variants);
  }, [variantSelection, showSelectedVariants, setSelectedVariants]);

  return (
    <SelectedVariantsContext.Provider
      value={{
        createTargetedVariant,
        variantSelection,
        selectedVariants,
        updateVariantSelection,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        refreshTargetedVariants,
        error,
        showSelectedVariants,
        setShowSelectedVariants,
      }}
    >
      {children}
    </SelectedVariantsContext.Provider>
  );
};
